import { createTheme, styled, ThemeProvider } from "@mui/material/styles";
import {
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Drawer as MobileDrawer,
  useMediaQuery,
  Avatar,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  Notifications as NotificationsIcon,
  AccountCircle as AccountCircleIcon,
} from "@mui/icons-material";
import { Menus } from "../router/Menus";
import React, { useEffect, useState } from "react";
import { useGetUser } from "../client/hooks/users/user.queries";
import MuiDrawer from "@mui/material/Drawer";
import Sidebar from "./components/sidebar/Sidebar";
import WelcomeSnackbar from "./components/WelcomeSnackbar";
import UserProfileDialogComponent from "./components/user/user.profile.component";
import CreateEcomStepperComponent from "./components/firstLoginComponents/CreateEconStepper.component.jsx";
import LoadingScreen from "../pages/Loading.page.jsx";
import CustomSnackbar from "../pages/components/Message.Component.jsx";
import WalaLogo from "../assets/wala.png";
import { useGetMarketplacePlans } from "../client/hooks/marketplace/marketplace.queries";
import { hasMenuAccess, getFirstAccessibleMenu } from "../utils/menuAccess";
const drawerWidth = 260;

// Styled AppBar para mobile
const MobileAppBar = styled(AppBar)(({ theme }) => ({
  background: "linear-gradient(135deg, #ff7043 0%, #ff6b35 100%)",
  boxShadow: "0 2px 16px rgba(255, 112, 67, 0.2)",
  backdropFilter: "blur(20px)",
  borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
  "& .MuiToolbar-root": {
    padding: theme.spacing(0, 1.5),
    minHeight: "64px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  position: "absolute",
  left: "50%",
  transform: "translateX(-50%)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const MobileActions = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.5),
  marginLeft: "auto",
}));

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

export const DrawerHeader = styled("div")(({ theme }) => ({
  ...theme.mixins.toolbar,
  position: "relative",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(2),
}));

export const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  "& .MuiDrawer-paper": {
    background: "linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%)",
    borderRight: "none",
    borderRadius: "0 16px 16px 0",
    boxShadow:
      "4px 0 20px rgba(255, 112, 67, 0.08), 0 0 40px rgba(0, 0, 0, 0.04)",
    width: drawerWidth,
    overflowX: "hidden",
    overflowY: "auto",
    backdropFilter: "blur(10px)",
    border: "1px solid rgba(255, 112, 67, 0.1)",
    transition: theme.transitions.create(["width", "box-shadow"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    "&:hover": {
      boxShadow:
        "6px 0 25px rgba(255, 112, 67, 0.12), 0 0 50px rgba(0, 0, 0, 0.06)",
    },
  },
  variants: [
    {
      props: ({ open }) => open,
      style: {
        ...openedMixin(theme),
        "& .MuiDrawer-paper": openedMixin(theme),
      },
    },
    {
      props: ({ open }) => !open,
      style: {
        ...closedMixin(theme),
        "& .MuiDrawer-paper": closedMixin(theme),
      },
    },
  ],
}));

export default function MainLayout() {
  const theme = createTheme({
    cssVariables: true,
    palette: {
      primary: {
        main: "#ff7043",
        light: "#ff9575",
        dark: "#c7401a",
      },
      secondary: {
        main: "#2196f3",
      },
      background: {
        default: "#f8f9fa",
        paper: "#ffffff",
      },
      text: {
        primary: "#1a1a1a",
        secondary: "#666",
      },
    },
    typography: {
      fontFamily: `"Inter", "Roboto", "Helvetica", "Arial", sans-serif`,
      fontSize: 14,
      fontWeightLight: 300,
      fontWeightRegular: 400,
      fontWeightMedium: 500,
      fontWeightBold: 700,
    },
    components: {
      MuiDrawer: {
        styleOverrides: {
          paper: {
            borderRadius: "0 16px 16px 0",
          },
        },
      },
    },
  });

  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [open, setOpen] = useState(
    localStorage.getItem("sidebarOpen") === "true",
  );
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [expandedMenu, setExpandedMenu] = useState(null);
  const storedLocation = JSON.parse(localStorage.getItem("location")) || {};
  const [selectedMenu, setSelectedMenu] = useState(storedLocation.menu ?? 0);
  const [selectedSubmenu, setSelectedSubmenu] = useState(
    storedLocation.submenu ?? null,
  );
  const [openProfile, setOpenProfile] = useState(false);
  const { data, isLoading, isError, error, refetch } = useGetUser();
  const [alert, setAlert] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [firtsLogin, setFirtsLogin] = useState(false);

  const [userName, setUserName] = useState("");
  const [showWelcome, setShowWelcome] = useState(false);
  const [openDialogEcom, setOpenDialogEcom] = useState(false);

  // Busca os planos do marketplace
  const { data: marketplacePlans = [] } = useGetMarketplacePlans();

  useEffect(() => {
    if (isError) {
      const errorMessage =
        error?.response?.data?.msg ||
        error?.message ||
        "Ocorreu um erro inesperado.";

      setErrorMsg(errorMessage);
      setAlert(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  useEffect(() => {
    const organization_id = localStorage.getItem("organization_id");
    if (organization_id === "") {
      setFirtsLogin(true);
      setOpenDialogEcom(true);
    }
  }, []);

  useEffect(() => {
    if (selectedMenu >= 0) {
      localStorage.setItem(
        "location",
        JSON.stringify({ menu: selectedMenu, submenu: selectedSubmenu }),
      );
    }
  }, [selectedMenu, selectedSubmenu]);

  useEffect(() => {
    setTimeout(() => {
      if (localStorage.getItem("firstLogin") === "true" && data) {
        const firstName = data.name.split(" ")[0];
        setUserName(firstName);
        setShowWelcome(true);
      }
    }, 1000);
    const storedLocation = localStorage.getItem("location");
    if (storedLocation) {
      const { menu } = JSON.parse(storedLocation);
      setSelectedMenu(menu);
    }
  }, [data]);
  const handleDrawerOpen = () => {
    localStorage.setItem("sidebarOpen", "true");
    setOpen(true);
  };

  const handleDrawerToggle = () => {
    if (isMobile) {
      setMobileDrawerOpen(!mobileDrawerOpen);
    } else {
      const newOpen = !open;
      localStorage.setItem("sidebarOpen", newOpen.toString());
      setOpen(newOpen);
      if (!newOpen) {
        setExpandedMenu(null);
      }
    }
  };

  const handleMobileDrawerClose = () => {
    setMobileDrawerOpen(false);
  };

  function handleToggleMenu(m, index) {
    if (!m.children) {
      setSelectedMenu(index);
      setSelectedSubmenu(null);
      localStorage.setItem("selectedMenu", index);
      localStorage.removeItem("selectedSubmenu");

      // Fechar drawer mobile após seleção
      if (isMobile) {
        setMobileDrawerOpen(false);
      }
    } else {
      if (!isMobile && !open) {
        handleDrawerOpen();
      }
      setExpandedMenu(expandedMenu === index ? null : index);
    }
  }
  function handleSubmenuClick(mIndex, submenuIndex) {
    setSelectedMenu(mIndex);
    setSelectedSubmenu(submenuIndex);
    localStorage.setItem("selectedMenu", mIndex);
    localStorage.setItem("selectedSubmenu", submenuIndex);

    // Fechar drawer mobile após seleção
    if (isMobile) {
      setMobileDrawerOpen(false);
    }
  }
  function handleLogout() {
    localStorage.clear();
    window.location.replace("/login");
  }

  function handleDialogClose() {
    setOpenProfile(false);
    refetch();
  }
  const handleCloseWelcome = () => {
    setShowWelcome(false);
  };

  const handleNavigateToTaxes = () => {
    setSelectedMenu(4);
    setSelectedSubmenu(2);
    localStorage.setItem("selectedMenu", "4");
    localStorage.setItem("selectedSubmenu", "2");
  };

  useEffect(() => {
    if (selectedSubmenu !== null) {
      localStorage.setItem("selectedSubmenu", selectedSubmenu.toString());
    }
  }, [selectedSubmenu]);

  // Redirecionamento automático para primeira tela liberada - executa no mount
  useEffect(() => {
    // Só executa se tiver planos carregados
    if (marketplacePlans.length > 0) {
      // Pega o menu/submenu atual do localStorage
      const storedLocation = JSON.parse(localStorage.getItem("location")) || {};
      const currentMenu = storedLocation.menu ?? 0;
      const currentSubmenu = storedLocation.submenu ?? null;

      // Verifica se a tela atual tem acesso
      const currentHasAccess =
        currentSubmenu !== null
          ? hasMenuAccess(currentMenu, currentSubmenu, marketplacePlans)
          : hasMenuAccess(currentMenu, null, marketplacePlans);

      if (!currentHasAccess) {
        // Se não tem acesso, redireciona para primeira tela liberada
        const firstAccessible = getFirstAccessibleMenu(
          Menus,
          marketplacePlans,
          data,
        );

        setSelectedMenu(firstAccessible.menu);
        setSelectedSubmenu(firstAccessible.submenu);

        // Atualiza localStorage
        localStorage.setItem(
          "location",
          JSON.stringify({
            menu: firstAccessible.menu,
            submenu: firstAccessible.submenu,
          }),
        );

        // Se o menu tem filhos, expande ele
        if (
          Menus[firstAccessible.menu]?.children &&
          firstAccessible.submenu !== null
        ) {
          setExpandedMenu(firstAccessible.menu);
        }
      }
    }
  }, [marketplacePlans]); // Executa quando os planos são carregados

  // Redirecionamento quando usuário tenta navegar para tela bloqueada
  useEffect(() => {
    // Só executa se tiver planos carregados
    if (marketplacePlans.length > 0) {
      // Verifica se a tela atual tem acesso
      const currentHasAccess =
        selectedSubmenu !== null
          ? hasMenuAccess(selectedMenu, selectedSubmenu, marketplacePlans)
          : hasMenuAccess(selectedMenu, null, marketplacePlans);

      if (!currentHasAccess) {
        // Se não tem acesso, redireciona para primeira tela liberada
        const firstAccessible = getFirstAccessibleMenu(
          Menus,
          marketplacePlans,
          data,
        );

        setSelectedMenu(firstAccessible.menu);
        setSelectedSubmenu(firstAccessible.submenu);

        // Atualiza localStorage
        localStorage.setItem(
          "location",
          JSON.stringify({
            menu: firstAccessible.menu,
            submenu: firstAccessible.submenu,
          }),
        );

        // Se o menu tem filhos, expande ele
        if (
          Menus[firstAccessible.menu]?.children &&
          firstAccessible.submenu !== null
        ) {
          setExpandedMenu(firstAccessible.menu);
        }
      }
    }
  }, [selectedMenu, selectedSubmenu, marketplacePlans]);
  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ display: "flex" }}>
        <WelcomeSnackbar
          open={showWelcome}
          onClose={handleCloseWelcome}
          userName={userName}
        />
        <CustomSnackbar
          open={alert}
          onClose={() => setAlert(false)}
          severity={isError ? "error" : "success"}
          message={isError ? errorMsg : ""}
        />

        {/* Header móvel */}
        {isMobile && (
          <MobileAppBar
            position="fixed"
            sx={{ zIndex: theme.zIndex.drawer + 1 }}
          >
            <Toolbar>
              <IconButton
                edge="start"
                color="inherit"
                aria-label="menu"
                onClick={handleDrawerToggle}
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: "12px",
                  backgroundColor: "rgba(255, 255, 255, 0.12)",
                  backdropFilter: "blur(10px)",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                    transform: "scale(1.04)",
                  },
                  "&:active": {
                    transform: "scale(0.96)",
                  },
                }}
              >
                <MenuIcon sx={{ fontSize: 22, color: "white" }} />
              </IconButton>

              <LogoContainer>{/* Espaço vazio para o logo */}</LogoContainer>

              <MobileActions>
                <IconButton
                  color="inherit"
                  sx={{
                    width: 38,
                    height: 38,
                    borderRadius: "10px",
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.18)",
                      transform: "translateY(-1px)",
                    },
                  }}
                >
                  <NotificationsIcon sx={{ fontSize: 19, color: "white" }} />
                </IconButton>
                <IconButton
                  color="inherit"
                  onClick={() => setOpenProfile(true)}
                  sx={{
                    width: 38,
                    height: 38,
                    borderRadius: "10px",
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.18)",
                      transform: "translateY(-1px)",
                    },
                  }}
                >
                  {data?.avatar ? (
                    <Avatar
                      src={data.avatar}
                      sx={{
                        width: 22,
                        height: 22,
                        border: "1px solid rgba(255, 255, 255, 0.3)",
                      }}
                    />
                  ) : (
                    <AccountCircleIcon sx={{ fontSize: 19, color: "white" }} />
                  )}
                </IconButton>
              </MobileActions>
            </Toolbar>
          </MobileAppBar>
        )}

        {/* Drawer móvel */}
        {isMobile ? (
          <MobileDrawer
            anchor="left"
            open={mobileDrawerOpen}
            onClose={handleMobileDrawerClose}
            ModalProps={{
              keepMounted: true,
            }}
            sx={{
              "& .MuiDrawer-paper": {
                width: drawerWidth,
                height: "100vh", // Altura fixa para o drawer móvel
                background: "linear-gradient(180deg, #ffffff 0%, #fafbfc 100%)",
                borderRight: "none",
                boxShadow:
                  "0 8px 32px rgba(255, 112, 67, 0.08), 0 0 0 1px rgba(255, 112, 67, 0.04)",
                borderTopRightRadius: "24px",
                borderBottomRightRadius: "24px",
                overflow: "hidden",
              },
              "& .MuiBackdrop-root": {
                backgroundColor: "rgba(0, 0, 0, 0.25)",
                backdropFilter: "blur(8px)",
              },
            }}
          >
            <Box
              sx={{
                height: "100vh", // Altura fixa para o container
                display: "flex",
                flexDirection: "column",
                paddingTop: "64px", // Padding para não sobrepor o header
              }}
            >
              {/* Header do drawer mobile */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "20px 24px",
                  borderBottom: "1px solid rgba(255, 112, 67, 0.08)",
                  background:
                    "linear-gradient(135deg, rgba(255, 112, 67, 0.03) 0%, rgba(255, 149, 117, 0.03) 100%)",
                  backdropFilter: "blur(10px)",
                  position: "relative",
                  flexShrink: 0,
                }}
              >
                {/* Logo centralizado - imagem completa */}
                <Box
                  component="img"
                  src={WalaLogo}
                  alt="Wala Logo"
                  sx={{
                    height: "50px",
                    width: "auto",
                    maxWidth: "120px",
                    objectFit: "contain",
                    filter: "drop-shadow(0 4px 8px rgba(255, 112, 67, 0.15))",
                    transition: "all 0.3s ease-in-out",
                    "&:hover": {
                      transform: "scale(1.05)",
                      filter:
                        "drop-shadow(0 6px 12px rgba(255, 112, 67, 0.25))",
                    },
                  }}
                />

                {/* Botão de fechar no canto superior direito */}
                <IconButton
                  onClick={handleMobileDrawerClose}
                  sx={{
                    position: "absolute",
                    top: "16px",
                    right: "16px",
                    width: 36,
                    height: 36,
                    borderRadius: "8px",
                    backgroundColor: "rgba(255, 112, 67, 0.08)",
                    color: "#ff7043",
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      backgroundColor: "rgba(255, 112, 67, 0.15)",
                      transform: "scale(1.05)",
                    },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>

              {/* Conteúdo do sidebar */}
              <Box
                sx={{
                  flex: 1,
                  overflow: "auto", // Permitir rolagem quando necessário
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0, // Importante para flexbox funcionar corretamente
                  paddingBottom: "60px", // Adicionar padding inferior para mostrar o botão de sair
                }}
              >
                <Sidebar
                  open={true}
                  drawerWidth={drawerWidth}
                  handleDrawerToggle={handleMobileDrawerClose}
                  selectedMenu={selectedMenu}
                  selectedSubmenu={selectedSubmenu}
                  expandedMenu={expandedMenu}
                  handleToggleMenu={handleToggleMenu}
                  handleSubmenuClick={handleSubmenuClick}
                  handleLogout={handleLogout}
                  handleNavigateToTaxes={handleNavigateToTaxes}
                  user={data}
                  isLoading={isLoading}
                  Menus={Menus}
                  theme={theme}
                  DrawerStyled={Box}
                  setOpenProfile={setOpenProfile}
                  isMobile={true}
                  marketplacePlans={marketplacePlans}
                  userData={data}
                />
              </Box>
            </Box>
          </MobileDrawer>
        ) : (
          <Sidebar
            open={open}
            drawerWidth={drawerWidth}
            handleDrawerToggle={handleDrawerToggle}
            selectedMenu={selectedMenu}
            selectedSubmenu={selectedSubmenu}
            expandedMenu={expandedMenu}
            handleToggleMenu={handleToggleMenu}
            handleSubmenuClick={handleSubmenuClick}
            handleLogout={handleLogout}
            handleNavigateToTaxes={handleNavigateToTaxes}
            user={data}
            isLoading={isLoading}
            Menus={Menus}
            theme={theme}
            DrawerStyled={Drawer}
            setOpenProfile={setOpenProfile}
            isMobile={false}
            marketplacePlans={marketplacePlans}
            userData={data}
          />
        )}

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: isMobile ? 2 : 3,
            backgroundColor: "#f8f9fa",
            minHeight: "100vh",
            paddingTop: isMobile ? "80px" : "24px",
            transition: theme.transitions.create(["margin-left", "padding"], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            ...(isMobile && {
              background: "linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)",
            }),
          }}
        >
          {firtsLogin && selectedMenu !== 3 ? (
            <LoadingScreen />
          ) : Menus[selectedMenu]?.children ? (
            Menus[selectedMenu]?.children[selectedSubmenu]?.component
          ) : (
            Menus[selectedMenu]?.component
          )}
        </Box>
      </Box>
      {openProfile && (
        <UserProfileDialogComponent
          open={openProfile}
          setOpen={handleDialogClose}
          user={data}
          isLoading={isLoading}
        />
      )}
      {openDialogEcom && (
        <CreateEcomStepperComponent
          open={openDialogEcom}
          isLoading={isLoading}
          user={data}
        />
      )}
    </ThemeProvider>
  );
}
